import { z } from 'zod';
import { ObjectIdSchema } from '../../shared/object-id.schema';
import { StepActionTypeSchema } from '../../shared/step-action-type.schema';
import { ProcessStepRecordingSchema } from '../process-step-recording';
import { EntityOrSuggestionRefSchema } from '../entity-suggestion';
import { AssigneeModelSchema } from '../../shared/assignee-model.schema';
const EditableStepFields = ProcessStepRecordingSchema.pick({
    inputs: true,
    task: true,
    rules: true,
    assignedTo: true,
    assignedToModel: true,
    helpers: true,
    whoElseDoesThis: true,
    generatedName: true,
    generatedDescription: true,
    manualCapacity: true
});
export const ProcessDraftStepSchema = EditableStepFields.extend({
    /**
   * Node-kind discriminator (ADR 0003): draft steps and draft events share
   * one collection. Defaults to 'step' so legacy payloads without the field
   * still parse.
   */ nodeKind: z.literal('step').default('step'),
    _id: ObjectIdSchema,
    draftId: ObjectIdSchema,
    processId: ObjectIdSchema,
    stableStepId: ObjectIdSchema,
    actionType: StepActionTypeSchema,
    /**
   * First-step / chronological-predecessor marker, mirroring
   * `ProcessStepRecording.previousStep`. Drafts seed this from the
   * corresponding live recording so the editor and flow builder can detect
   * "this is the first step" without consulting input-level tags — a first
   * step points at its start event NODE id. Mutations that change the step
   * graph (insert-before, disconnect-from-start) update the field. Publish
   * writes it through to the live step.
   */ previousStep: z.union([
        ObjectIdSchema,
        z.null()
    ]).optional(),
    supersedesAssignment: ObjectIdSchema.optional(),
    /**
   * Creation-time placement hint pointing at an existing swimlane key (e.g.
   * `dept-<id>`, `team-<id>`). Lets a freshly-added step render in the user's
   * intended lane while `assignedTo` is still empty. Cleared by the server
   * whenever `assignedTo` is set or unset on the step — the real assignee
   * always wins, and an explicit unassignment removes any lingering hint.
   */ laneKey: z.string().optional(),
    /**
   * Preserves assignee across mode conversions: if the user converts this
   * step to an assignment and later flips back, we restore these fields so
   * their picked person/orgFunction isn't lost. Symmetric with
   * `ProcessDraftAssignmentSchema.stashedStep`.
   */ stashedAssignment: z.object({
        assignedTo: EntityOrSuggestionRefSchema.optional(),
        assignedToModel: AssigneeModelSchema.optional()
    }).optional(),
    /**
   * Draft-only staging flag (ADR 0009): when true, publishing this step
   * re-requests its surveys — every current performer's completed survey is
   * reset to pending. Set per-step (or for all steps) in the editor; never
   * takes effect live, only on publish. Consumed when the draft is torn down at
   * publish, so it never persists onto the live recording.
   */ resurveyRequested: z.boolean().optional(),
    updatedAt: z.date()
});

//# sourceMappingURL=process-draft-step.schema.js.map